import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

const TermsPage = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 1, 2026";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 sticky top-0 bg-background/95 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-lg font-semibold">Terms of Use</h1>
        </div>
      </header>

      <ScrollArea className="h-[calc(100vh-65px)]">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="prose prose-invert prose-sm max-w-none">
            <p className="text-muted-foreground text-sm mb-8">Last Updated: {lastUpdated}</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground mb-4">
                By accessing or using Arlo (&quot;Service&quot;), you agree to be bound by these Terms of Use (&quot;Terms&quot;). 
                If you disagree with any part of these terms, you do not have permission to access the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground mb-4">
                Arlo is an AI-powered personal assistant designed to help business professionals with tasks including 
                but not limited to: drafting communications, planning projects, brainstorming ideas, and decision support.
              </p>
              <p className="text-muted-foreground mb-4">
                The Service uses artificial intelligence to generate responses based on your inputs. While we strive for 
                accuracy, AI-generated content may contain errors or inaccuracies. You are responsible for reviewing and 
                verifying any output before use.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. User Accounts</h2>
              <p className="text-muted-foreground mb-4">
                To access certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. Acceptable Use</h2>
              <p className="text-muted-foreground mb-4">You agree NOT to use the Service to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Generate content that is illegal, harmful, threatening, abusive, or harassing</li>
                <li>Impersonate any person or entity</li>
                <li>Transmit malware, viruses, or other harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Use automated means to access the Service without permission</li>
                <li>Generate content that infringes intellectual property rights</li>
                <li>Create content promoting violence, discrimination, or illegal activities</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. Subscription and Payments</h2>
              <p className="text-muted-foreground mb-4">
                <strong>Free Tier:</strong> Limited features with 5 messages per day, 3 conversations, and 5 memories.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Pro Subscription:</strong> Paid plans provide unlimited access to features. Subscriptions 
                automatically renew unless cancelled before the renewal date.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Refunds:</strong> We do not offer refunds for partial subscription periods. You may cancel 
                at any time, and your subscription will remain active until the end of the current billing period.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. Intellectual Property</h2>
              <p className="text-muted-foreground mb-4">
                <strong>Our Content:</strong> The Service, including its design, features, and underlying technology, 
                is owned by Arlo and protected by intellectual property laws.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Your Content:</strong> You retain ownership of content you input into the Service. By using 
                the Service, you grant us a limited license to process your content to provide the Service.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>AI Output:</strong> Content generated by the AI is provided for your use. You are responsible 
                for ensuring your use complies with applicable laws and third-party rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground mb-4">
                THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER 
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS 
                FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="text-muted-foreground mb-4">
                We do not warrant that the Service will be uninterrupted, error-free, or secure. AI-generated 
                content may contain inaccuracies and should not be relied upon as professional advice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Limitation of Liability</h2>
              <p className="text-muted-foreground mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ARLO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED 
                DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p className="text-muted-foreground mb-4">
                Our total liability for any claims arising from these Terms or the Service shall not exceed 
                the amount you paid us in the twelve (12) months preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. Indemnification</h2>
              <p className="text-muted-foreground mb-4">
                You agree to indemnify and hold harmless Arlo and its officers, directors, employees, and agents 
                from any claims, damages, losses, or expenses arising from your use of the Service or violation 
                of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. Termination</h2>
              <p className="text-muted-foreground mb-4">
                We may terminate or suspend your account and access to the Service immediately, without prior 
                notice, for conduct that we believe violates these Terms or is harmful to other users, us, or 
                third parties, or for any other reason at our sole discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. Changes to Terms</h2>
              <p className="text-muted-foreground mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of material changes 
                by posting the updated Terms on the Service with a new &quot;Last Updated&quot; date. Continued use of 
                the Service after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. Governing Law</h2>
              <p className="text-muted-foreground mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of 
                Delaware, United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. Contact Information</h2>
              <p className="text-muted-foreground mb-4">
                For questions about these Terms, please contact us at: <a href="mailto:legal@heyarlo.ai" className="text-emerald-400 hover:underline">legal@heyarlo.ai</a>
              </p>
            </section>
          </div>
        </main>
      </ScrollArea>
    </div>
  );
};

export default TermsPage;
